import React, { useState } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { Text } from '../components/Texte';
import { SafeAreaView } from 'react-native-safe-area-context';
import { ShieldCheck, Bell, Trash2, GraduationCap } from '../components/icons';
import { SettingsRow } from '../components/SettingsRow';
import { useGabarit } from '../hooks/useGabarit';
import { ScreenHeader } from '../components/ScreenHeader';
import { UiLanguagePicker } from '../components/UiLanguagePicker';
import { t } from '../i18n';
import { useAuth } from '../hooks/useAuth';
import { useIsAdmin } from '../hooks/useIsAdmin';
import { authErrorText } from '../utils/authErrors';
import { colors, screenPadding, spacing, typography } from '../theme/tokens';
import { confirmer } from '../utils/confirmation';
import { ParametresLarge } from './ParametresLarge';

export interface SettingsScreenProps {
  onBack?: () => void;
  onOpenPrivacy: () => void;
  onOpenDataDeletion: () => void;
  onOpenNotifications: () => void;
  onOpenAdmin?: () => void;
  onOpenLicenceEcole?: () => void;
}

export function SettingsScreen({ onBack, onOpenPrivacy, onOpenDataDeletion, onOpenNotifications, onOpenAdmin, onOpenLicenceEcole }: SettingsScreenProps) {
  const { signOut, deleteAccount } = useAuth();
  const { isAdmin } = useIsAdmin();
  const [deleting, setDeleting] = useState(false);

  // Suppression réussie → la session est effacée → retour automatique à l'écran d'auth.
  const runDelete = async () => {
    setDeleting(true);
    try {
      const result = await deleteAccount();
      if (result.errorKey) confirmer(t('settings.deleteFailedTitle'), authErrorText(result.errorKey));
    } finally {
      setDeleting(false);
    }
  };

  const confirmDelete = () => {
    confirmer(t('settings.deleteConfirmTitle'), t('settings.deleteConfirmBody'), [
      { text: t('common.cancel'), style: 'cancel' },
      { text: t('common.delete'), style: 'destructive', onPress: () => void runDelete() },
    ]);
  };

  const { large } = useGabarit();
  const sCompte = (
    <>
        {/* Infos personnelles, abonnement et aide sont REMONTÉS sur le Profil : les laisser ici
            aussi créerait deux chemins vers la même chose. Ne restent que les écrans qu'on consulte
            rarement, et les actions de sortie. */}
        <Section title={t('settings.sections.account')}>
          {/* Licence donnée par l'école de l'étudiant (code d'invitation). Aucune promotion d'achat ici. */}
          {onOpenLicenceEcole ? (
            <SettingsRow icon={GraduationCap} accent="blue" label={t('settings.licenceEcole')} onPress={onOpenLicenceEcole} testID="settings-licence-ecole" />
          ) : null}
          <SettingsRow icon={ShieldCheck} accent="blue" label={t('settings.privacy')} onPress={onOpenPrivacy} testID="settings-privacy" />
          <SettingsRow icon={Trash2} accent="blue" label={t('settings.dataDeletion')} onPress={onOpenDataDeletion} testID="settings-data-deletion" />
        </Section>

            </>
  );
  const sPrefs = (
    <>
{/* PRÉFÉRENCES de notification, à ne pas confondre avec la cloche du Profil qui ouvre la
            LISTE : deux écrans distincts, cette ligne n'est donc pas un doublon. */}
        <Section title={t('settings.sections.preferences')}>
          <SettingsRow icon={Bell} accent="blue" label={t('settings.notifications')} onPress={onOpenNotifications} testID="settings-notifications" />
          <UiLanguagePicker />
        </Section>

            </>
  );
  const sAdmin = (
    <>
{isAdmin && onOpenAdmin ? (
          <Section title={t('settings.sections.admin')}>
            <SettingsRow icon={ShieldCheck} accent="lime" label={t('settings.adminDashboard')} onPress={onOpenAdmin} testID="settings-admin" />
          </Section>
        ) : null}

            </>
  );
  const sSortie = (
    <>
<View style={styles.group}>
          <SettingsRow label={t('settings.signOut')} variant="danger" showChevron={false} onPress={signOut} testID="account-signout" />
        </View>

            </>
  );
  const sDanger = (
    <>
<Section title={t('settings.sections.danger')}>
          <SettingsRow
            label={deleting ? t('settings.deleting') : t('settings.deleteAccount')}
            variant="danger"
            showChevron={false}
            onPress={deleting ? () => {} : confirmDelete}
            testID="account-delete"
          />
        </Section>
    </>
  );

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <ScreenHeader onBack={onBack} title={t('settings.title')} testID="settings-back" />
      {/* Ordinateur et tablette : cartes par famille (ParametresLarge). Le téléphone garde sa pile. */}
      {large ? (
        <ParametresLarge
          onOpenPrivacy={onOpenPrivacy}
          onOpenDataDeletion={onOpenDataDeletion}
          onOpenNotifications={onOpenNotifications}
          onSignOut={signOut}
          onDeleteAccount={confirmDelete}
          deleting={deleting}
          onOpenLicenceEcole={onOpenLicenceEcole}
          onOpenAdmin={isAdmin ? onOpenAdmin : undefined}
        />
      ) : (
        <ScrollView contentContainerStyle={styles.content}>
          {sCompte}
          {sPrefs}
          {sAdmin}
          {sSortie}
          {sDanger}
        </ScrollView>
      )}
    </SafeAreaView>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>{title.toUpperCase()}</Text>
      <View style={styles.group}>{children}</View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.grey50 },
  content: { padding: screenPadding, gap: spacing.lg },
  section: { gap: spacing.sm },
  sectionTitle: {
    fontSize: typography.caption.size,
    fontWeight: typography.caption.weight,
    letterSpacing: typography.caption.letterSpacing,
    color: colors.grey500,
    paddingHorizontal: spacing.xs,
  },
  group: { gap: spacing.sm },
});
